import AddIcon from "@mui/icons-material/Add";
import CalculateIcon from "@mui/icons-material/Calculate";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import DatePicker from "../components/datePicker/DatePickerV2";

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import { getCalculation } from "../calculations/calculationsApi";
import { lastCalculationVersion } from "../calculations/EditCalculationPage";
import { useDeleteCalculation } from "../calculations/useCalculation";
import type { ListCalculationsResponseItemWithCustomer } from "../calculations/useCalculations";
import { useCalculations } from "../calculations/useCalculations";
import AccountManagerSelect from "../components/accountManagerSelect/AccountManagerSelect";
import { AsyncLoadingIconButton } from "../components/asyncLoadingButton/AsyncLoadingIconButton";
import CustomerLink from "../components/customerLink/CustomerLink";
import CustomerSelect from "../components/customerSelect/CustomerSelect";
import { useCustomers } from "../customers/useCustomers";
import dayjs from "../dayjs";
import { startOfPreviousMonth } from "../misc/dateUtils";
import { customerSelectSx } from "./components/customerSelectSx";
import { StatusChip } from "./components/StatusChip";
import { useDeleteCalculationConfirmationDialog } from "./components/useDeleteCalculationConfirmationDialog";
import type { DuplicateFromState, NewCalculationPageLocationState } from "./NewCalculationPage";

type StatusFilter = "all" | "active" | "ordered" | "value" | "customersOwn";
type SortColumn = "name" | "startDate" | "createdAt" | "customer" | "status";
type SortDirection = "asc" | "desc";

interface SortConfig {
  column: SortColumn;
  direction: SortDirection;
}

interface Filters {
  text: string;
  status: StatusFilter;
  customer: string;
  accountManager: { id: string; customers: string[] } | null;
  dateFrom: string;
  dateTo: string;
  createdAtFrom: string;
  createdAtTo: string;
}

const headerCellSx = {
  fontWeight: "bold",
  color: "text.secondary",
  fontSize: "0.8rem",
  textTransform: "uppercase" as const,
  letterSpacing: 0.5,
};

function formatDate(dateStr: string) {
  if (!dateStr) return "";
  try {
    return new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" }).format(
      new Date(dateStr),
    );
  } catch {
    return dateStr;
  }
}

function filterCalculations(
  calculations: ListCalculationsResponseItemWithCustomer[],
  filters: Filters,
  customerContacts: string[],
): ListCalculationsResponseItemWithCustomer[] {
  return calculations.filter((c) => {
    const matchesText =
      !filters.text ||
      c.name.toLowerCase().includes(filters.text.toLowerCase()) ||
      c.customer.name?.toLowerCase().includes(filters.text.toLowerCase()) ||
      c.id.toLowerCase().includes(filters.text.toLowerCase());
    const isOrdered = !!c.orderId;
    const hasPrice = c.price && typeof c.price === "number";
    const isGeneratedByCustomer = customerContacts.find((cc) => cc === c.createdBy);
    const matchesStatus =
      filters.status === "all" ||
      (filters.status === "ordered" && isOrdered) ||
      (filters.status === "active" && !isOrdered) ||
      (filters.status === "value" && hasPrice) ||
      (filters.status === "customersOwn" && isGeneratedByCustomer);
    const matchesCustomer = !filters.customer || c.customer.id == filters.customer;
    const matchesAccountManager =
      !filters.accountManager || filters.accountManager.customers.find((ac) => ac == c.customer.id);
    const matchesDateFrom = !filters.dateFrom || c.startDate >= filters.dateFrom;
    const matchesDateTo = !filters.dateTo || c.startDate <= filters.dateTo;
    const matchesCreatedAtFrom = !filters.createdAtFrom || !c.createdAt || c.createdAt >= filters.createdAtFrom;
    const matchesCreatedAtTo = !filters.createdAtTo || !c.createdAt || c.createdAt <= filters.createdAtTo;
    return (
      matchesText &&
      matchesStatus &&
      matchesCustomer &&
      matchesDateFrom &&
      matchesDateTo &&
      matchesAccountManager &&
      matchesCreatedAtFrom &&
      matchesCreatedAtTo
    );
  });
}

const sortColumnLabels: Record<SortColumn, string> = {
  name: "Kalkulacja",
  startDate: "Data wyjazdu",
  createdAt: "Data wygenerowania",
  customer: "Klient",
  status: "Status",
};

function sortCalculations(
  calculations: ListCalculationsResponseItemWithCustomer[],
  sort: SortConfig,
): ListCalculationsResponseItemWithCustomer[] {
  return [...calculations].sort((a, b) => {
    let cmp = 0;
    if (sort.column === "name") {
      cmp = a.name.localeCompare(b.name, "pl");
    } else if (sort.column === "startDate") {
      cmp = a.startDate.localeCompare(b.startDate);
    } else if (sort.column === "createdAt") {
      cmp = a.createdAt && b.createdAt ? a.createdAt.localeCompare(b.createdAt!) : 0;
    } else if (sort.column === "customer") {
      cmp = (a.customer.name ?? "").localeCompare(b.customer.name ?? "", "pl");
    } else if (sort.column === "status") {
      cmp = (a.orderId ? 1 : 0) - (b.orderId ? 1 : 0);
    }
    return sort.direction === "asc" ? cmp : -cmp;
  });
}
const filterPanels: {
  status: StatusFilter;
  label: string;
  color: string;
  bg: string;
  adminOnly: boolean;
}[] = [
  {
    status: "all",
    label: "Wszystkie",
    color: "#3557a1",
    bg: "rgba(53, 87, 161, 0.08)",
    adminOnly: false,
  },
  {
    status: "active",
    label: "W toku",
    color: "#0d6efd",
    bg: "rgba(13, 110, 253, 0.08)",
    adminOnly: false,
  },
  {
    status: "ordered",
    label: "Zamówione",
    color: "#198754",
    bg: "rgba(25, 135, 84, 0.08)",
    adminOnly: false,
  },
  {
    status: "value",
    label: "Wartość",
    color: "#198754",
    bg: "rgba(25, 135, 84, 0.08)",
    adminOnly: false,
  },
  {
    status: "customersOwn",
    label: "% kalkulacji własnych",
    color: "#ef940b",
    bg: "rgba(25, 135, 84, 0.08)",
    adminOnly: true,
  },
];
export function CalculationsList() {
  const navigate = useNavigate();
  // const { openChildInNewTab } = useOpenInNewTab();
  const { context } = useAuthContext();
  const { calculations, isLoading } = useCalculations({ from: startOfPreviousMonth().format("YYYY-MM") });
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const { customers } = useCustomers();
  const [filters, setFilters] = useState<Filters>({
    text: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
    createdAtFrom: "",
    createdAtTo: "",
    customer: "",
    accountManager: null,
  });
  const [sort, setSort] = useState<SortConfig>({ column: "startDate", direction: "asc" });
  const isCustomer = context?.role === "CUSTOMER";

  const customerContacts = !isCustomer
    ? customers
        .flatMap((c) => c.contacts ?? [])
        .map((c) => c.email)
        .filter((c): c is string => !!c)
    : [];
  function handleSort(column: SortColumn) {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  }
  const deleteCalculationConfirmationDialog = useDeleteCalculationConfirmationDialog();
  const deleteCalculation = useDeleteCalculation();

  const handleDuplicate = async (calc: ListCalculationsResponseItemWithCustomer) => {
    setDuplicatingId(calc.id);
    try {
      const full = await getCalculation(calc.id);
      const version = lastCalculationVersion(full);
      const duplicateFrom: DuplicateFromState = {
        name: version.name,
        startDate: version.startDate,
        participantsNumber: version.participantsNumber,
        products: version.products.map((p) => ({
          id: p.id,
          daysShift: p.daysShift ?? 0,
          options: p.options.map((o) => ({ id: o.id, quantity: o.quantity })),
        })),
        currency: version.currency ?? undefined,
        margin: version.margin,
        packageId: version.packageId,
        note: version.note,
        customer: calc.customer.name ? { id: calc.customer.id, name: calc.customer.name } : null,
        isPackageVersion: full.type === "PACKAGE",
      };
      navigate("new", { state: { duplicateFrom } satisfies NewCalculationPageLocationState });
    } finally {
      setDuplicatingId(null);
    }
  };

  async function handleDelete(calculationId: string) {
    if (await deleteCalculationConfirmationDialog.open()) {
      await deleteCalculation.mutateAsync({ calculationId });
    }
  }

  const isAdminOrAgent = context?.role === "ADMIN" || context?.role === "AGENT";
  const filteredCalculations = sortCalculations(filterCalculations(calculations, filters, customerContacts), sort);
  const calculationsValue = filteredCalculations
    .filter((c): c is typeof c & { price: number } => typeof c.price === "number")
    .reduce((sum, c) => sum + (c.currency === "PLN" ? c.price / 4.3 : c.price), 0)
    .toFixed(0);
  const calculationsGeneratedByCustomer = filteredCalculations.filter((c) =>
    customerContacts.find((cc) => c.createdBy && cc === c.createdBy),
  ).length;
  const percentageOfCalculationsGeneratedByCustomer =
    filteredCalculations.length == 0
      ? 0
      : ((calculationsGeneratedByCustomer / filteredCalculations.length) * 100).toFixed(0);
  const stats: Record<StatusFilter, number | string> = {
    all: filteredCalculations.length,
    active: filteredCalculations.filter((c) => !c.orderId).length,
    ordered: filteredCalculations.filter((c) => !!c.orderId).length,
    value: `${calculationsValue} EUR`,
    customersOwn: `${percentageOfCalculationsGeneratedByCustomer} %`,
  };
  const colSpan = isAdminOrAgent ? 5 : 4;

  return (
    <Box sx={{ marginTop: "1.5em", pb: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
          }}
        >
          Kalkulacje
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
          }}
        >
          Zarządzaj swoimi kalkulacjami cenowymi. Twórz nowe wyceny i śledź ich status.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: isCustomer ? "repeat(4, 1fr)" : "repeat(5, 1fr)" },
          gap: 2,
          mb: 4,
        }}
      >
        {filterPanels
          .filter((stat) => !isCustomer || !stat.adminOnly)
          .map((stat) => {
            const isSelected = filters.status === stat.status;
            return (
              <Paper
                key={stat.label}
                elevation={0}
                onClick={() => setFilters((prev) => ({ ...prev, status: stat.status }))}
                sx={{
                  p: 2.5,
                  cursor: "pointer",
                  bgcolor: isSelected ? stat.bg : "background.paper",
                  borderRadius: 2,
                  border: `1px solid ${isSelected ? stat.color : "rgba(0,0,0,0.06)"}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: stat.color,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    letterSpacing: 0.5,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: stat.color,
                    mt: 0.5,
                  }}
                >
                  {stats[stat.status]}
                </Typography>
              </Paper>
            );
          })}
      </Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          useFlexGap
          sx={{ flex: 1, flexWrap: "wrap", rowGap: 2 }}
        >
          <TextField
            placeholder="Szukaj kalkulacji..."
            size="small"
            value={filters.text}
            onChange={(e) => setFilters((f) => ({ ...f, text: e.target.value }))}
            sx={{ minWidth: { sm: 280 }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {!isCustomer && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <CustomerSelect
                customerId={filters.customer || null}
                onChange={(customerId) => {
                  setFilters((f) => ({ ...f, customer: customerId ?? "" }));
                }}
                size="small"
                sx={{ ...customerSelectSx, width: 280, minWidth: 280, flexShrink: 0 }}
              />
            </Box>
          )}

          {!isCustomer && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <AccountManagerSelect
                accountManagerId={filters.accountManager?.id || null}
                onChange={(accountManagerId, customerIds) => {
                  setFilters((f) => ({
                    ...f,
                    accountManager:
                      accountManagerId && customerIds ? { id: accountManagerId, customers: customerIds } : null,
                  }));
                }}
                size="small"
                sx={{ ...customerSelectSx, width: 220, minWidth: 220, flexShrink: 0 }}
              />
            </Box>
          )}

          <TextField
            select
            size="small"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as StatusFilter }))}
            sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          >
            <MenuItem value="all">Wszystkie statusy</MenuItem>
            <MenuItem value="active">W toku</MenuItem>
            <MenuItem value="ordered">Zamówione</MenuItem>
          </TextField>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <DatePicker
              label="Wyjazd od"
              size="small"
              width={155}
              value={filters.dateFrom ? dayjs(filters.dateFrom) : null}
              maxDate={filters.dateTo ? dayjs(filters.dateTo) : undefined}
              onChange={(d) => setFilters((f) => ({ ...f, dateFrom: d?.toISOString() ?? "" }))}
            />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              —
            </Typography>
            <DatePicker
              label="Wyjazd do"
              size="small"
              width={155}
              value={filters.dateTo ? dayjs(filters.dateTo) : null}
              minDate={filters.dateFrom ? dayjs(filters.dateFrom) : undefined}
              onChange={(d) => setFilters((f) => ({ ...f, dateTo: d?.toISOString() ?? "" }))}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <DatePicker
              label="Wygenerowana od"
              size="small"
              width={185}
              value={filters.createdAtFrom ? dayjs(filters.createdAtFrom) : null}
              maxDate={filters.createdAtTo ? dayjs(filters.createdAtTo) : undefined}
              onChange={(d) => setFilters((f) => ({ ...f, createdAtFrom: d?.toISOString() ?? "" }))}
            />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              —
            </Typography>
            <DatePicker
              label="Wygenerowana do"
              size="small"
              width={185}
              value={filters.createdAtTo ? dayjs(filters.createdAtTo) : null}
              minDate={filters.createdAtFrom ? dayjs(filters.createdAtFrom) : undefined}
              onChange={(d) => setFilters((f) => ({ ...f, createdAtTo: d?.toISOString() ?? "" }))}
            />
          </Stack>
        </Stack>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={() => navigate("new")}
          sx={{
            py: 1,
            px: 3,
            whiteSpace: "nowrap",
            borderRadius: 2,
            bgcolor: "#3557a1",
            "&:hover": { bgcolor: "#2a4585" },
          }}
        >
          Nowa kalkulacja
        </Button>
      </Stack>
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}
      <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                <TableCell sx={headerCellSx}>
                  <TableSortLabel
                    active={sort.column === "name"}
                    direction={sort.column === "name" ? sort.direction : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Kalkulacja
                  </TableSortLabel>
                </TableCell>
                {isAdminOrAgent && (
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={sort.column === "customer"}
                      direction={sort.column === "customer" ? sort.direction : "asc"}
                      onClick={() => handleSort("customer")}
                    >
                      Klient
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sort.column === "startDate"}
                    direction={sort.column === "startDate" ? sort.direction : "asc"}
                    onClick={() => handleSort("startDate")}
                  >
                    Data wyjazdu
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sort.column === "createdAt"}
                    direction={sort.column === "createdAt" ? sort.direction : "asc"}
                    onClick={() => handleSort("createdAt")}
                  >
                    Data Wygenerowania
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>
                  <TableSortLabel
                    active={sort.column === "status"}
                    direction={sort.column === "status" ? sort.direction : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "center", width: 140 }}>Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCalculations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} sx={{ textAlign: "center", py: 6 }}>
                    <CalculateIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1, display: "block", mx: "auto" }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {isLoading
                        ? "Ładowanie kalkulacji..."
                        : "Nie znaleziono kalkulacji spełniających kryteria wyszukiwania"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCalculations.map((calc) => (
                  <TableRow
                    key={calc.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      transition: "background-color 0.15s ease",
                      "&:last-child td": { borderBottom: 0 },
                    }}
                    onClick={() => navigate(calc.id)}
                  >
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            mb: 0.25,
                            color: "#3557a1",
                          }}
                        >
                          {calc.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.disabled",
                          }}
                        >
                          {calc.createdBy}
                        </Typography>
                      </Box>
                    </TableCell>
                    {isAdminOrAgent && (
                      <TableCell>
                        <CustomerLink customerId={calc.customer.id} color="inherit" underline="hover" variant="body2">
                          {calc.customer.name}
                        </CustomerLink>
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                          alignItems: "center",
                        }}
                      >
                        <CalendarMonthIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        <Typography variant="body2">{formatDate(calc.startDate)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                          alignItems: "center",
                        }}
                      >
                        <CalendarMonthIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        <Typography variant="body2">{calc.createdAt ? formatDate(calc.createdAt) : "?"}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <StatusChip orderId={calc.orderId} />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Podgląd kalkulacji">
                          <IconButton
                            size="small"
                            sx={{ color: "#3557a1", bgcolor: "rgba(53, 87, 161, 0.06)" }}
                            onClick={() => navigate(calc.id)}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplikuj kalkulację">
                          <IconButton
                            size="small"
                            sx={{ color: "text.secondary" }}
                            onClick={() => handleDuplicate(calc)}
                            disabled={duplicatingId === calc.id}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={calc.orderId ? "Nie można usunąć zamówionej kalkulacji" : "Usuń kalkulację"}>
                          <span>
                            <AsyncLoadingIconButton
                              size="small"
                              sx={{ color: "text.secondary" }}
                              onClick={async () => await handleDelete(calc.id)}
                              disabled={!!calc.orderId}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </AsyncLoadingIconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid rgba(0,0,0,0.06)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Wyświetlono <strong>{filteredCalculations.length}</strong> z <strong>{calculations.length}</strong>{" "}
            kalkulacji
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
            }}
          >
            Sortowanie: {sortColumnLabels[sort.column]} ({sort.direction === "asc" ? "rosnąco" : "malejąco"})
          </Typography>
        </Box>
      </Paper>
      {deleteCalculationConfirmationDialog.portal}
    </Box>
  );
}
